import { Clock, Instagram, MapPin, Navigation, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useLang } from "@/i18n";
import { useContent } from "@/content/ContentContext";

const ContattiSection = () => {
  const { t } = useLang();
  const c = t.contatti;
  const { site, waLink, images } = useContent();

  return (
    <section id="contatti" className="py-24 md:py-36 border-t border-sand/70" data-testid="contatti-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-7">
          <Reveal>
            <p className="eyebrow text-olive mb-7">{c.label}</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              {c.title}
            </h2>
            <p className="mt-8 max-w-lg text-base md:text-lg font-light leading-relaxed text-anthracite/85">
              {c.text}
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="btn-primary"
              >
                <WhatsAppIcon className="w-4 h-4" />
                {c.cta}
              </a>
              <a
                href={site.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-link"
                className="btn-outline"
              >
                <Navigation className="w-4 h-4" />
                {c.directions}
              </a>
            </div>
            <p className="mt-5 text-sm text-anthracite/60 max-w-md leading-relaxed" data-testid="contact-hint">
              {c.hint}
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <dl className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 mt-0.5 text-bordeaux" aria-hidden="true" />
                <div>
                  <dt className="text-xs uppercase tracking-[0.2em] text-anthracite/50">{c.phoneLabel}</dt>
                  <dd className="mt-1">
                    <a
                      href={site.phoneHref}
                      data-testid="contact-phone-link"
                      className="text-lg hover:text-bordeaux transition-colors duration-200"
                    >
                      {site.phoneDisplay}
                    </a>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 mt-0.5 text-bordeaux" aria-hidden="true" />
                <div>
                  <dt className="text-xs uppercase tracking-[0.2em] text-anthracite/50">{c.addressLabel}</dt>
                  <dd className="mt-1 text-lg" data-testid="contact-address">{site.address}</dd>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 mt-0.5 text-bordeaux" aria-hidden="true" />
                <div>
                  <dt className="text-xs uppercase tracking-[0.2em] text-anthracite/50">{c.hoursLabel}</dt>
                  <dd className="mt-1 text-lg text-anthracite/55 italic" data-testid="contact-hours">
                    {site.hours || c.hoursPlaceholder}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Instagram className="w-5 h-5 mt-0.5 text-bordeaux" aria-hidden="true" />
                <div>
                  <dt className="text-xs uppercase tracking-[0.2em] text-anthracite/50">Instagram</dt>
                  <dd className="mt-1 text-lg" data-testid="contact-instagram">
                    {site.instagram ? (
                      <a
                        href={site.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-bordeaux transition-colors duration-200"
                      >
                        @umagazin
                      </a>
                    ) : (
                      <span className="text-anthracite/45 italic">{c.instagramPlaceholder}</span>
                    )}
                  </dd>
                </div>
              </div>
            </dl>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="md:col-span-5">
          <div className="frame md:mt-10">
            <img
              src={images.contatti.door}
              data-edit-img="contatti.door"
              alt={c.altDoor}
              loading="lazy"
              className="w-full aspect-[3/4] object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ContattiSection;
